import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  IconPlus,
  IconPencil,
  IconUserStar,
  IconUsersGroup,
  IconFolders,
  IconCalendarTime,
  IconClipboardList,
  IconMedicalCross,
  IconEdit,
} from "@tabler/icons-react";
import IconBubble from "@/components/icons/IconBubble";
import MAvatar from "@/components/ui/atoms/MAvatar";
import MBadge from "@/components/ui/atoms/MBadge";
import { MButton } from "@/components/ui/atoms/MButtons";
import MButtonCard from "@/components/ui/atoms/MButtonCard";
import MaatraLogo from "@/components/ui/atoms/MaatraLogo";
import MHeader from "@/components/ui/organisms/MHeader";
import MInputField from "@/components/ui/atoms/MInputField";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MDialog from "@/components/ui/atoms/MDialog";
import MLoader from "@/components/ui/atoms/MLoader";
import { useFamily } from "@/hooks/useFamily";
import { getMemberInfo } from "@/hooks/useMembers";
import { useNotesContext } from "@/context/NotesContext";
import { useFamilyStats } from "@/hooks/useFamilyStats";
import { useMemberAccess } from "@/hooks/useMemberAccess";
import { usePendingAccessRequests } from "@/hooks/usePendingAccessRequests";
import { clearSelectedMember } from "@/store/authSlice";
import { setNavigationSource } from "@/utils/navigationSource";
import FamilyNotesButtonWithBadge from "./FamilyNotesButtonWithBadge";
import { MTourTrigger } from "@/components/ui/organisms/MTourTrigger";
import { MHelpIcon } from "@/components/ui/atoms/MHelpIcon";
import VitalsBanner from "./VitalsBanner";
import styles from "./FamilyLanding.module.css";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

interface FamilyMember {
  id?: string;
  _id?: string;
  first_name: string;
  last_name?: string;
  nickname?: string;
  registered_member: boolean;
  registration_status?: string;
  relationship?: string | number;
  avatar?: string;
  family_member_type?: string;
}

const FamilyLanding: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tourTriggerRef = React.useRef<any>(null);
  const {
    data: familyMembers = [],
    family,
    updateFamilyName,
    isFamilyOwner,
    managedIds,
    managedLoading,
  } = useFamily();
  const loggedInMember = getMemberInfo(true);
  const memberInfo = getMemberInfo(true);
  const { fetchUnreadCount } = useNotesContext();
  const { familyStats, fetchFamilyStats } = useFamilyStats({ memberInfo });
  const {
    requestingAccess,
    handleMemberClick: handleMemberClickFromHook,
    handleRequestAccessWithCleanup,
  } = useMemberAccess();

  const [isEditingFamilyName, setIsEditingFamilyName] = useState(false);
  const [familyNameInput, setFamilyNameInput] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [showNoAccessDialog, setShowNoAccessDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [selectedMemberForAccess, setSelectedMemberForAccess] =
    useState<FamilyMember | null>(null);
  const { pendingRequests } = usePendingAccessRequests({
    familyMembers,
    managedIds,
    familyMemberType: memberInfo.family_member_type,
    enabled: true,
  });

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (memberInfo?.id) {
      console.log("Fetching family stats");
      fetchFamilyStats();
    }
  }, [
    memberInfo?.id,
    family?._id,
    memberInfo?.current_family,
    fetchFamilyStats,
  ]);

  // Set navigation source when visiting family landing page
  useEffect(() => {
    setNavigationSource("family-landing", "/");
  }, []);

  // Clear selectedMember when returning to family screens
  useEffect(() => {
    localStorage.removeItem("selectedMember");
    localStorage.removeItem("add_member_form");
    dispatch(clearSelectedMember());
  }, [dispatch]);

  // Track GTM user_data_loaded event on page load
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_HOME,
    });
  }, [memberInfo?.id]);

  const familyName = family?.name || "Your family";
  const totalMembers = familyMembers.length;

  // Show loader while family data is loading
  const isLoading = managedLoading || !family || familyMembers.length === 0;

  const handleAddMember = () => {
    // Track add member click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_ADD_MEMBER_RELATION,
    });

    navigate("/profile/family/add-member");
  };

  const handleEditFamilyName = () => {
    setFamilyNameInput(family?.name || "");
    setIsEditingFamilyName(true);

    // Track edit family name click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_FAMILY_NAME_EDIT,
    });
  };

  const handleMemberClick = (member: FamilyMember) => {
    const memberId = member.id || member._id;
    const loggedInId = loggedInMember.id || (loggedInMember as any)._id;

    // Track member profile click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_MEMBER_HOME,
    });

    handleMemberClickFromHook({
      memberId,
      loggedInMemberId: loggedInId,
      loggedInMemberType: loggedInMember.family_member_type || "",
      managedIds,
      managedLoading,
      onNoAccess: () => {
        setSelectedMemberForAccess(member);
        setShowNoAccessDialog(true);
      },
    });
  };

  const handleShowConsentDialog = () => {
    setShowNoAccessDialog(false);
    setShowConsentDialog(true);
  };

  const handleConfirmRequestAccess = async () => {
    if (!selectedMemberForAccess || !family) return;

    const memberId = loggedInMember.id || (loggedInMember as any)._id;
    const familyMemberId =
      selectedMemberForAccess.id || selectedMemberForAccess._id;
    const familyId = family._id || family.id;

    await handleRequestAccessWithCleanup(
      { memberId, familyId, familyMemberId },
      () => {
        setShowConsentDialog(false);
        setSelectedMemberForAccess(null);
      }
    );

    // Note: pending requests state is now managed by usePendingAccessRequests hook
    // The hook will automatically re-fetch when dependencies change
  };

  const isOwner = (member: FamilyMember) => {
    return member.family_member_type === "fo";
  };

  const getMemberDisplayName = (member: FamilyMember) => {
    const memberId = member.id || member._id;
    const loggedInId = loggedInMember.id || (loggedInMember as any)._id;

    if (memberId === loggedInId) {
      return "You";
    }
    return member.nickname || member.first_name;
  };

  const renderMemberBadge = (member: FamilyMember) => {
    const displayName = getMemberDisplayName(member);
    const owner = isOwner(member);
    const hasCheckmark = member.registered_member;

    return (
      <button
        key={member.id || member._id}
        className={`${styles.memberItem} ${
          owner ? styles.memberItemOwner : ""
        }`}
        onClick={() => handleMemberClick(member)}
        type="button"
      >
        <div className={styles.avatarWrapper}>
          <MAvatar
            size="lg"
            type={member.avatar ? "dp" : "no-dp"}
            src={member.avatar}
            text={`${member.first_name || ""}${
              member.last_name ? " " + member.last_name : ""
            }`.trim()}
            textSize={24}
            badgeIcon={owner ? <IconUserStar size={16} /> : undefined}
            className={
              member.registered_member || owner
                ? styles.avatarRegistered
                : styles.avatarUnregistered
            }
          />
          {hasCheckmark && !owner && (
            <div className={styles.checkmarkBadge}>
              <MaatraLogo size={12} fill="#FBFBFB" />
            </div>
          )}
        </div>
        {owner && <MBadge className={styles.ownerBadgeLabel}>Owner</MBadge>}
        <div
          className={styles.memberName}
          style={{ marginTop: owner ? "-4px" : "-8px" }}
        >
          {displayName}
        </div>
      </button>
    );
  };

  const renderEmptySlot = () => {
    return (
      <button
        className={styles.emptySlot}
        onClick={handleAddMember}
        type="button"
      >
        <div className={styles.emptyCircle}>
          <IconPlus size={24} stroke={1.5} color="#4C189A" />
        </div>
      </button>
    );
  };

  // Show loader while family data is loading
  if (isLoading) {
    return <MLoader size={32} color="#8764BB" />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerHelpIcon} data-wizard-help-icon>
        <MHelpIcon onClick={() => tourTriggerRef.current?.open()} />
      </div>
      {/* Sticky header section with gradient background */}
      <div className={styles.stickyHeader}>
        {/* Header section: Name, Description, Pencil icon */}
        <div className={styles.headerRow}>
          <span
            className="heading-h7-bold"
            style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {familyName}
          </span>
          {isFamilyOwner && (
            <IconEdit
              color="#45168C"
              size={20}
              style={{ cursor: "pointer" }}
              onClick={handleEditFamilyName}
              data-testid="edit-family-name"
            />
          )}
          <FooterDrawer
            open={isEditingFamilyName}
            onClose={() => setIsEditingFamilyName(false)}
            title="Edit family name"
            doneText="Done"
            donePosition="top"
            allowKeyboard
            doneDisabled={
              updateLoading ||
              !familyNameInput.trim() ||
              familyNameInput.trim() === family?.name
            }
            onDone={async () => {
              if (
                !familyNameInput.trim() ||
                familyNameInput.trim() === family?.name
              )
                return;
              setUpdateLoading(true);
              try {
                await updateFamilyName(familyNameInput.trim());
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
                  screenName:
                    ANALYTICS_SCREEN_NAMES.FAMILY_INFO_FAMILY_NAME_EDIT_COMPLETE,
                });
                setIsEditingFamilyName(false);
              } catch (err) {
                console.error(err);
              } finally {
                setUpdateLoading(false);
              }
            }}
            height="70vh"
          >
            <div style={{ padding: 24 }}>
              <MInputField
                variant="Underlined"
                size="md"
                value={familyNameInput}
                showLabel
                label="Family name"
                onChange={(e) => setFamilyNameInput(e.target.value)}
                autoFocus
                className="w-full"
                placeholder="Not Answered"
              />
            </div>
          </FooterDrawer>
        </div>

        <div style={{ marginTop: -12, padding: "0", marginBottom: 24 }}>
          <MHeader
            className="family-header-gradient"
            module="Family"
            memberInfo={memberInfo}
            title=""
            description="View and manage your family group, health records, and member access."
            learnMoreContent={
              <>
                As the Family Owner, you can add or remove family members,
                invite them to join your family group, and manage access for
                other member accounts.
                <br />
                <br />
                You can also add family notes to capture important health
                details or reminders. Family notes are shared with all family
                members.
              </>
            }
            drawerTitle="Learn More"
          />
        </div>

        <div className={styles.actionButtonsContainer}>
          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            {(loggedInMember.family_member_type === "fo" ||
              loggedInMember.family_member_type === "fm") && (
              <MButton
                data-add-member-button
                variant="outline"
                size="sm"
                state="default"
                style={{ width: "49%" }}
                leftIcon={<IconPlus size={18} />}
                onClick={() => {
                  localStorage.removeItem("add_member_form");
                  handleAddMember();
                }}
              >
                <span className="body-base-semibold whitespace-nowrap">
                  Add Member
                </span>
              </MButton>
            )}
            {/* Family Notes button - full width for FM, 49% for FO */}
            <div
              style={{
                width:
                  loggedInMember.family_member_type === "fm" ? "100%" : "49%",
              }}
            >
              <FamilyNotesButtonWithBadge
                loggedInMember={loggedInMember}
                navigate={navigate}
              />
            </div>
          </div>
        </div>
      </div>
      {/* End of sticky section */}

      <div className={styles.content}>
        <VitalsBanner
          onClick={() => navigate("/vitals-tracker")}
          className={styles.vitalsBanner}
        />

        {/* Family Grid Section Container */}
        <div className={styles.familyGridContainer}>
          {/* Members Count */}
          <div className={styles.membersHeader}>
            <div className={styles.membersCount}>
              <IconUsersGroup size={24} stroke={1.5} />
              <span>{totalMembers} members</span>
            </div>
            <button className={styles.tapToView}>
              {/* <IconInfoCircle size={20} stroke={2} color="#407ED0" /> */}
              <span>Tap to view profile</span>
            </button>
          </div>

          {/* Members Grid */}
          <div className={styles.membersGrid}>
            {/* Sort members to show logged-in member (You) first, then alphabetically */}
            {[...familyMembers]
              .sort((a, b) => {
                const aId = a.id || a._id;
                const bId = b.id || b._id;
                const loggedInId =
                  loggedInMember.id || (loggedInMember as any)._id;

                // Logged-in user always first
                if (aId === loggedInId) return -1;
                if (bId === loggedInId) return 1;

                // Sort others alphabetically by first name
                const aName = (a.first_name || "").toLowerCase();
                const bName = (b.first_name || "").toLowerCase();
                return aName.localeCompare(bName);
              })
              .map((member: FamilyMember) => renderMemberBadge(member))}
            {/* Only show Add icon for Family Owner */}
            {renderEmptySlot()}
          </div>
        </div>

        {/* Family Overview Section */}
        <div className={styles.familyOverviewSection}>
          {/* Title Row */}
          <div className={styles.overviewHeader}>
            <h2 className={styles.overviewTitle}>Family overview</h2>
            <button className={styles.tapToView}>
              {/* <IconInfoCircle size={20} stroke={2} color="#407ED0" /> */}
              <span>Tap to view</span>
            </button>
          </div>

          {/* Count Cards Row */}
          <div className={styles.countCardsRow}>
            <MButtonCard
              title="Documents"
              count={familyStats?.totalDocs ?? 0}
              icon={<IconFolders />}
              size="small"
              onClick={() => navigate("/profile/family/documents-overview")}
            />
            <MButtonCard
              title="Memories"
              count={familyStats?.totalHealthMemories ?? 0}
              icon={<IconBubble color="#8764BB" />}
              size="small"
              onClick={() =>
                navigate("/profile/family/health-memories-overview")
              }
            />
            <MButtonCard
              title="Reminders"
              count={familyStats?.totalReminders ?? 0}
              icon={<IconCalendarTime />}
              size="small"
              onClick={() => navigate("/profile/family/reminders-overview")}
            />
          </div>

          {/* Ready Cards Row */}
          <div className={styles.readyCardsRow}>
            <MButtonCard
              title="Emergency cards ready"
              count={familyStats?.totalEmergencyCardReady ?? 0}
              icon={<IconMedicalCross />}
              size="large"
              onClick={() =>
                navigate("/profile/family/emergency-cards-overview")
              }
            />
            <MButtonCard
              title="Health profiles complete"
              count={familyStats?.totalHealthProfileCompleted ?? 0}
              icon={<IconClipboardList />}
              size="large"
              onClick={() =>
                navigate("/profile/family/health-profiles-overview")
              }
            />
          </div>
        </div>
      </div>

      {/* No Access Dialog */}
      <MDialog
        isOpen={showNoAccessDialog}
        onClose={() => {
          setShowNoAccessDialog(false);
          setSelectedMemberForAccess(null);
        }}
        title="No profile access"
        description="You don't have access to this family member's profile. You can ask the Family Owner for access if you need it."
        primaryCtaText={
          pendingRequests[
            selectedMemberForAccess?.id || selectedMemberForAccess?._id || ""
          ]
            ? "Access Requested"
            : "Ask for access"
        }
        secondaryCtaText="Cancel"
        onPrimaryCta={handleShowConsentDialog}
        onSecondaryCta={() => {
          setShowNoAccessDialog(false);
          setSelectedMemberForAccess(null);
        }}
        isPrimaryCtaDisabled={
          pendingRequests[
            selectedMemberForAccess?.id || selectedMemberForAccess?._id || ""
          ]
        }
      />

      {/* Consent Confirmation Dialog */}
      <MDialog
        isOpen={showConsentDialog}
        onClose={() => {
          setShowConsentDialog(false);
          setSelectedMemberForAccess(null);
        }}
        title="Consent needed"
        description="Please confirm that you have this family member's consent to view their health information."
        primaryCtaText="Confirm"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleConfirmRequestAccess}
        onSecondaryCta={() => {
          setShowConsentDialog(false);
          setSelectedMemberForAccess(null);
        }}
        isPrimaryCtaLoading={requestingAccess}
      />

      <MTourTrigger
        ref={tourTriggerRef}
        page={
          loggedInMember?.family_member_type === "fo"
            ? "family-owner"
            : "family-member"
        }
      />
    </div>
  );
};

export default FamilyLanding;
